"use client";
import { useEffect, useRef, useState } from "react";
import {
  ArrowUpRight,
  Upload,
  Video,
  Check,
  Link2,
  Loader2,
  X,
  Eye,
  Send,
  RefreshCw,
} from "lucide-react";
import type { Creator } from "@/lib/tiktok/rules";
import { MAX_VIDEO_BYTES } from "@/lib/tiktok/rules";

type Connection = {
  configured: boolean;
  approved: boolean;
  connected: boolean;
  signedIn: boolean;
};
type Pending = { ticket: string; caption: string; status: string };
const labels: Record<string, string> = {
  PUBLIC_TO_EVERYONE: "Everyone",
  MUTUAL_FOLLOW_FRIENDS: "Friends",
  FOLLOWER_OF_CREATOR: "Followers",
  SELF_ONLY: "Only me",
};
async function request(path: string, body?: unknown) {
  const response = await fetch(`/api/tiktok/${path}`, {
    method: body === undefined ? "GET" : "POST",
    ...(body !== undefined
      ? {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      : {}),
    cache: "no-store",
  });
  const data = await response.json();
  if (!response.ok)
    throw new Error(data.error || "Something went wrong. Please try again.");
  return data;
}
export default function Publisher() {
  const [connection, setConnection] = useState<Connection | null>(null);
  const [creator, setCreator] = useState<Creator | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState("");
  const [duration, setDuration] = useState(0);
  const [title, setTitle] = useState("");
  const [privacy, setPrivacy] = useState("");
  const [comments, setComments] = useState(false);
  const [duet, setDuet] = useState(false);
  const [stitch, setStitch] = useState(false);
  const [commercial, setCommercial] = useState(false);
  const [ownBrand, setOwnBrand] = useState(false);
  const [branded, setBranded] = useState(false);
  const [aiGenerated, setAiGenerated] = useState(false);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [pending, setPending] = useState<Pending | null>(null);
  const [dragging, setDragging] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const mounted = useRef(true);
  const operation = useRef(false);

  async function loadConnection() {
    try {
      const data: Connection = await request("session");
      if (!mounted.current) return;
      setConnection(data);
      if (data.connected) {
        const info = await request("creator", {});
        if (mounted.current) {
          setCreator(info.creator);
          setPrivacy("");
        }
      } else {
        setCreator(null);
        setPrivacy("");
      }
    } catch (e) {
      if (mounted.current)
        setError(e instanceof Error ? e.message : "Connection unavailable.");
    }
  }
  useEffect(() => {
    mounted.current = true;
    loadConnection();
    try {
      const saved = sessionStorage.getItem("peerquik-pending");
      if (saved) setPending(JSON.parse(saved));
    } catch {}
    const result = new URLSearchParams(window.location.search).get(
      "connection",
    );
    if (result === "expired")
      setError("The connection request expired. Connect TikTok again and approve the new request within 10 minutes.");
    if (result === "failed")
      setError("TikTok connection failed. Please try again.");
    if (result === "cancelled")
      setNotice("Connection cancelled. Your video has not been sent.");
    if (result === "setup")
      setNotice(
        "TikTok connection is not available yet. You can still prepare your video here.",
      );
    if (result === "success")
      setNotice("TikTok connected. Check the account below before posting.");
    if (result) window.history.replaceState({}, "", "/publish");
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (!file) {
      setPreview("");
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  useEffect(() => {
    try {
      if (pending)
        sessionStorage.setItem("peerquik-pending", JSON.stringify(pending));
      else sessionStorage.removeItem("peerquik-pending");
    } catch {}
  }, [pending]);
  // Consent is specific to the current content and settings.
  useEffect(
    () => setConsent(false),
    [
      file,
      title,
      privacy,
      comments,
      duet,
      stitch,
      commercial,
      ownBrand,
      branded,
      aiGenerated,
    ],
  );
  useEffect(() => {
    if (
      !pending ||
      !pending.ticket ||
      !connection?.connected ||
      ["PUBLISH_COMPLETE", "FAILED"].includes(pending.status)
    )
      return;
    const timer = setTimeout(() => checkStatus(), 5000);
    return () => clearTimeout(timer);
  }, [pending, connection]);
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    if (busy) window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [busy]);
  function selectFile(chosen?: File) {
    if (!chosen || busy) return;
    if (
      chosen.type !== "video/mp4" &&
      !chosen.name.toLowerCase().endsWith(".mp4")
    ) {
      setError("Choose an MP4 video.");
      return;
    }
    if (chosen.size > MAX_VIDEO_BYTES || chosen.size < 1) {
      setError("Choose a video smaller than 500 MB.");
      return;
    }
    setError("");
    setDuration(0);
    setFile(chosen);
    setProgress(0);
  }
  async function checkStatus() {
    if (!pending || !pending.ticket || operation.current) return;
    try {
      const data = await request("status", { ticket: pending.ticket });
      if (!mounted.current) return;
      setPending({ ...pending, status: data.status });
      if (data.status === "FAILED")
        setError(
          `TikTok could not publish this post${data.fail_reason ? ` (${String(data.fail_reason).replace(/_/g, " ")})` : ""}. Review it before starting a new post.`,
        );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not check this post.");
    }
  }
  async function disconnect() {
    if (operation.current) return;
    operation.current = true;
    setBusy(true);
    setError("");
    try {
      await request("disconnect", {});
      setCreator(null);
      setConnection((prev) => (prev ? { ...prev, connected: false } : prev));
      setPending(null);
      setPrivacy("");
      setConsent(false);
      setNotice("TikTok disconnected and authorization revoked.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not disconnect.");
    } finally {
      operation.current = false;
      setBusy(false);
    }
  }
  const valid = !!(
    file &&
    duration > 0 &&
    creator &&
    privacy &&
    consent &&
    (!commercial || ownBrand || branded) &&
    !(commercial && branded && privacy === "SELF_ONLY") &&
    duration <= creator.max_video_post_duration_sec &&
    !pending
  );
  async function publish() {
    if (!valid || !file || operation.current) return;
    operation.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    setProgress(0);
    let job: Pending | null = null;
    try {
      const initialized = await request("init", {
        size: file.size,
        duration,
        title,
        privacy,
        comments,
        duet,
        stitch,
        commercial,
        ownBrand,
        branded,
        aiGenerated,
        consent,
      });
      const form = new FormData();
      for (const [name, value] of Object.entries(initialized.fields))
        form.append(name, String(value));
      form.append("file", file);
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", initialized.uploadUrl);
        xhr.timeout = 600000;
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable)
            setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () =>
          xhr.status >= 200 && xhr.status < 300
            ? resolve()
            : reject(
                new Error(
                  "Video storage could not accept this upload. Please try again.",
                ),
              );
        xhr.onerror = () =>
          reject(
            new Error(
              "Video upload failed. Check your connection and try again.",
            ),
          );
        xhr.ontimeout = () =>
          reject(new Error("Video upload timed out. Please try again."));
        xhr.send(form);
      });
      job = { ticket: "", caption: title, status: "CHECK_REQUIRED" };
      setPending(job);
      const submitted = await request("submit", { ticket: initialized.ticket });
      job = {
        ticket: submitted.statusTicket,
        caption: title,
        status: "PROCESSING_DOWNLOAD",
      };
      setPending(job);
      setNotice(
        "Video sent. TikTok may take a few minutes to process it and make it visible according to your chosen audience.",
      );
      setConsent(false);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Could not publish. Check the post status before retrying.",
      );
      if (job) setPending({ ...job, status: "CHECK_REQUIRED" });
    } finally {
      operation.current = false;
      setBusy(false);
    }
  }
  const postStatus =
    pending?.status === "PUBLISH_COMPLETE"
      ? "Published on TikTok"
      : pending?.status === "FAILED"
        ? "Post failed"
        : pending?.status === "CHECK_REQUIRED"
          ? "Submission needs checking. Review your TikTok account."
          : "TikTok is processing your video";
  return (
    <div className="pq-workspace">
      <div className="pq-workspace-heading">
        <div>
          <div className="pq-eyebrow">YOUR CREATIVE DESK</div>
          <h1>A new post, a fresh start</h1>
          <p>Bring the video. Add the details. Make it yours.</p>
        </div>
        <span className="pq-beta-pill">Early access</span>
      </div>
      {error && (
        <div className="pq-alert pq-alert-error" role="alert">
          {error}
          <button aria-label="Dismiss error" onClick={() => setError("")}>
            <X size={16} />
          </button>
        </div>
      )}
      {notice && (
        <div className="pq-alert" role="status">
          {notice}
        </div>
      )}
      {connection && !connection.approved && (
        <div className="pq-approval-note">
          <span className="pq-live-dot" />
          <div>
            <b>
              {connection.configured
                ? "Private testing mode"
                : "The editor is ready. TikTok connection is coming."}
            </b>
            <p>
              {connection.configured
                ? "Public posting awaits TikTok approval. Tests require a private TikTok account and Only me visibility."
                : "Prepare and preview your video now. Connecting an account and sending posts will be available after TikTok setup; public publishing also requires approval."}
            </p>
          </div>
        </div>
      )}
      <div className="pq-editor-grid">
        <section className="pq-editor-main">
          <div className="pq-card">
            <div className="pq-card-heading">
              <span className="pq-step-number">01</span>
              <h2>Your video</h2>
              <span>MP4 · up to 500 MB</span>
            </div>
            {file ? (
              <div className="pq-selected-file">
                <Video size={26} />
                <div>
                  <b>{file.name}</b>
                  <small>
                    {(file.size / 1_000_000).toFixed(1)} MB
                    {duration
                      ? ` · ${Math.round(duration)} seconds`
                      : " · Reading video…"}
                  </small>
                </div>
                <button
                  disabled={busy}
                  onClick={() => {
                    setFile(null);
                    setDuration(0);
                  }}
                  aria-label="Remove video"
                >
                  <X size={18} />
                </button>
              </div>
            ) : (
              <button
                className={`pq-dropzone ${dragging ? "pq-dragging" : ""}`}
                disabled={busy}
                onClick={() => input.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  selectFile(e.dataTransfer.files[0]);
                }}
              >
                <span className="pq-upload-icon">
                  <Upload size={26} />
                </span>
                <b>Drop your next TikTok here</b>
                <span>
                  or <u>browse files</u> to get started
                </span>
                <small>
                  Your video stays on this device until you publish.
                </small>
              </button>
            )}
            <input
              ref={input}
              type="file"
              accept="video/mp4,.mp4"
              hidden
              disabled={busy}
              onChange={(e) => {
                selectFile(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            {file && (
              <button
                type="button"
                disabled={busy}
                className="pq-inline-button"
                onClick={() => input.current?.click()}
              >
                Choose another video
              </button>
            )}
          </div>
          <div className="pq-card">
            <div className="pq-card-heading">
              <span className="pq-step-number">02</span>
              <h2>The finishing touches</h2>
            </div>
            <fieldset disabled={busy} className="pq-fieldset">
              <label className="pq-label" htmlFor="caption">
                Caption
              </label>
              <textarea
                id="caption"
                placeholder="What’s the story? Add a caption, #hashtags or @mentions…"
                maxLength={2200}
                rows={4}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <div className="pq-input-help">
                <span>A little context goes a long way.</span>
                <span>{title.length} / 2,200</span>
              </div>
              <label className="pq-label" htmlFor="visibility">
                Who can watch this video?
              </label>
              <select
                id="visibility"
                value={privacy}
                disabled={!creator}
                onChange={(e) => setPrivacy(e.target.value)}
              >
                <option value="">
                  {creator
                    ? "Choose visibility"
                    : "Connect TikTok to see your options"}
                </option>
                {creator?.privacy_level_options.map((option) => (
                  <option
                    value={option}
                    key={option}
                    disabled={commercial && branded && option === "SELF_ONLY"}
                  >
                    {labels[option] || option}
                  </option>
                ))}
              </select>
              {commercial && branded && (
                <small className="pq-field-note">
                  Branded content visibility cannot be set to private.
                </small>
              )}
              <label className="pq-label">Let people interact</label>
              <div className="pq-interactions">
                {[
                  {
                    name: "Comments",
                    value: comments,
                    set: setComments,
                    disabled: !creator || creator.comment_disabled,
                  },
                  {
                    name: "Duet",
                    value: duet,
                    set: setDuet,
                    disabled: !creator || creator.duet_disabled,
                  },
                  {
                    name: "Stitch",
                    value: stitch,
                    set: setStitch,
                    disabled: !creator || creator.stitch_disabled,
                  },
                ].map((item) => (
                  <label
                    key={item.name}
                    className={item.disabled ? "pq-disabled" : ""}
                  >
                    <input
                      type="checkbox"
                      checked={item.value && !item.disabled}
                      disabled={item.disabled}
                      onChange={(e) => item.set(e.target.checked)}
                    />
                    {item.name}
                  </label>
                ))}
              </div>
              <small className="pq-field-note">
                Available settings follow your TikTok account preferences.
              </small>
              <div className="pq-disclosure">
                <label className="pq-check-row">
                  <input
                    type="checkbox"
                    checked={commercial}
                    onChange={(e) => {
                      setCommercial(e.target.checked);
                      setOwnBrand(false);
                      setBranded(false);
                    }}
                  />
                  <span>
                    <b>Disclose commercial content</b>
                    <small>
                      This video promotes you, a brand, a product or a service.
                    </small>
                  </span>
                </label>
                {commercial && (
                  <div className="pq-disclosure-options">
                    <label className="pq-check-row">
                      <input
                        type="checkbox"
                        checked={ownBrand}
                        onChange={(e) => setOwnBrand(e.target.checked)}
                      />
                      <span>
                        <b>Your brand</b>
                        <small>Promoting yourself or your own business.</small>
                      </span>
                    </label>
                    <label
                      className={`pq-check-row ${privacy === "SELF_ONLY" ? "pq-disabled" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={branded}
                        disabled={privacy === "SELF_ONLY"}
                        onChange={(e) => setBranded(e.target.checked)}
                      />
                      <span>
                        <b>Branded content</b>
                        <small>
                          Promoting another brand or a third party. Visibility
                          cannot be private.
                        </small>
                      </span>
                    </label>
                    {(ownBrand || branded) && (
                      <p className="pq-field-note">
                        Your video will be labeled as “
                        {branded ? "Paid partnership" : "Promotional content"}”.
                      </p>
                    )}
                  </div>
                )}
              </div>
              <label className="pq-check-row pq-ai">
                <input
                  type="checkbox"
                  checked={aiGenerated}
                  onChange={(e) => setAiGenerated(e.target.checked)}
                />
                <span>
                  <b>AI-generated content</b>
                  <small>
                    Label realistic content generated or significantly edited
                    with AI.
                  </small>
                </span>
              </label>
            </fieldset>
          </div>
        </section>
        <aside className="pq-editor-side">
          <div className="pq-card pq-account-card">
            <div className="pq-card-heading">
              <Link2 size={18} />
              <h2>Your TikTok account</h2>
            </div>
            {connection === null ? (
              <p className="pq-loading">
                <Loader2 className="pq-spin" size={16} /> Checking connection…
              </p>
            ) : creator ? (
              <>
                <div className="pq-account">
                  <span className="pq-avatar">
                    {creator.creator_nickname?.slice(0, 1) || "T"}
                  </span>
                  <div>
                    <b>{creator.creator_nickname}</b>
                    <small>@{creator.creator_username}</small>
                  </div>
                  <Check size={17} />
                </div>
                <p className="pq-field-note">
                  Videos up to {creator.max_video_post_duration_sec} seconds.
                </p>
                <button
                  className="pq-inline-button"
                  disabled={busy}
                  onClick={disconnect}
                >
                  Disconnect account
                </button>
              </>
            ) : (
              <>
                <p>
                  Connect the account you want to post to. You’ll approve access
                  on TikTok.
                </p>
                {connection.connected && (
                  <button
                    className="pq-inline-button"
                    disabled={busy}
                    onClick={loadConnection}
                  >
                    <RefreshCw size={14} /> Retry account details
                  </button>
                )}
                <a
                  className="pq-button pq-button-outline"
                  href="/api/tiktok/connect"
                  aria-disabled={!connection.configured}
                  onClick={(e) => {
                    if (!connection.configured) {
                      e.preventDefault();
                      setNotice(
                        "TikTok connection is not available yet. You can prepare and preview your video in the meantime.",
                      );
                    }
                  }}
                >
                  Connect TikTok <ArrowUpRight size={17} />
                </a>
                {!connection.configured && (
                  <small className="pq-field-note">
                    Connection is not available yet.
                  </small>
                )}
              </>
            )}
          </div>
          <div className="pq-card pq-preview-card">
            <div className="pq-card-heading">
              <Eye size={18} />
              <h2>A final look</h2>
            </div>
            <div
              className={`pq-video-preview ${preview ? "pq-has-video" : ""}`}
            >
              {preview ? (
                <video
                  key={preview}
                  src={preview}
                  controls
                  playsInline
                  preload="metadata"
                  onLoadedMetadata={(e) => {
                    const v = e.currentTarget;
                    if (!Number.isFinite(v.duration) || v.duration <= 0) {
                      setError(
                        "This video could not be read. Choose another MP4.",
                      );
                      return;
                    }
                    if (
                      v.videoWidth < 360 ||
                      v.videoHeight < 360 ||
                      v.videoWidth > 4096 ||
                      v.videoHeight > 4096
                    ) {
                      setError(
                        "Video dimensions must be between 360 and 4096 pixels.",
                      );
                      setDuration(0);
                      return;
                    }
                    setDuration(v.duration);
                  }}
                  onError={() => {
                    setDuration(0);
                    setError(
                      "This video could not be played. Use an MP4 encoded with H.264.",
                    );
                  }}
                />
              ) : (
                <>
                  <Video size={32} />
                  <b>Your next great post</b>
                  <span>Video preview appears here</span>
                </>
              )}
            </div>
            {title && <p className="pq-caption-preview">{title}</p>}
            <fieldset disabled={busy} className="pq-fieldset">
              <label className="pq-check-row pq-consent">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />
                <span>
                  I authorize Peerquik to send this video and these settings to
                  my connected TikTok account. By posting, you agree to TikTok’s{" "}
                  {commercial && branded && (
                    <>
                      <a
                        href="https://www.tiktok.com/legal/page/global/bc-policy/en"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Branded Content Policy
                      </a>{" "}
                      and{" "}
                    </>
                  )}
                  <a
                    href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Music Usage Confirmation
                  </a>
                  .
                </span>
              </label>
              <button
                className="pq-button pq-publish-button"
                disabled={!valid || busy}
                onClick={publish}
              >
                {busy ? (
                  <>
                    <Loader2 className="pq-spin" size={17} />
                    {progress > 0 ? `Uploading ${progress}%` : "Please wait…"}
                  </>
                ) : (
                  <>
                    Post to TikTok <Send size={17} />
                  </>
                )}
              </button>
            </fieldset>
            {busy && (
              <progress
                max={100}
                value={progress}
                aria-label="Video upload progress"
              />
            )}
            <small className="pq-publish-help">
              {!creator
                ? "Connect TikTok before publishing."
                : !file
                  ? "Choose a video to get started."
                  : !privacy
                    ? "Choose who can watch your video."
                    : !duration
                      ? "Waiting for a playable video."
                      : duration > creator.max_video_post_duration_sec
                        ? "This video exceeds your account’s duration limit."
                        : commercial && !ownBrand && !branded
                          ? "Indicate whether this promotes your brand, a third party, or both."
                          : !consent
                            ? "Review your video and confirm your consent."
                            : "TikTok may take a few minutes to process your post."}
            </small>
          </div>
        </aside>
      </div>
      {pending && (
        <section className="pq-card pq-post-status" aria-live="polite">
          <div>
            <span className="pq-eyebrow">LATEST POST · THIS BROWSER TAB</span>
            <h2>{postStatus}</h2>
            <p>{pending.caption || "Your video"}</p>
            <small>
              Keep this tab open during upload. Status checks remain available
              here for 24 hours.
            </small>
          </div>
          <div className="pq-status-actions">
            <button
              className="pq-button pq-button-outline"
              disabled={busy || !pending.ticket}
              onClick={checkStatus}
            >
              <RefreshCw size={16} /> Check status
            </button>
            {(["PUBLISH_COMPLETE", "FAILED"].includes(pending.status) ||
              !pending.ticket) && (
              <button
                className="pq-inline-button"
                onClick={() => {
                  if (
                    !pending.ticket &&
                    !window.confirm(
                      "This does not cancel a TikTok submission. Check your account first to avoid posting the same video twice. Start a new post?",
                    )
                  )
                    return;
                  setPending(null);
                  setFile(null);
                  setDuration(0);
                  setTitle("");
                  setPrivacy("");
                  setConsent(false);
                  setError("");
                  setNotice("");
                }}
              >
                Start a new post
              </button>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
